<template>
  <modal
    name="drive-share"
    height="auto"
    width="680px"
    :scrollable="true"
    :adaptive="true"
    class="modal-dialog"
    @closed="$emit('close')"
  >
    <div class="modal-content o-common-modal-content">
      <div class="modal-header text-center">
        <button type="button" class="close" @click="closeModal">
          <i class="fa fa-times" aria-hidden="true"></i>
        </button>
        <h4 class="modal-title">
          <i class="fa fa-share-alt" aria-hidden="true"></i>
          Share "{{ file.name }}"
        </h4>
      </div>
      <div class="modal-body" v-loading="loadingPerms">
        <!-- Tabs: Groups → Status → Skills → Users -->
        <ul class="nav nav-tabs drive-share-tabs">
          <li :class="{ active: activeTab === 'groups' }">
            <a href="javascript: void(0)" @click="activeTab = 'groups'">
              <i class="fa fa-users" aria-hidden="true"></i> Groups
            </a>
          </li>
          <li :class="{ active: activeTab === 'status' }">
            <a href="javascript: void(0)" @click="activeTab = 'status'">
              <i class="fa fa-tag" aria-hidden="true"></i> Status
            </a>
          </li>
          <li :class="{ active: activeTab === 'skills' }">
            <a href="javascript: void(0)" @click="activeTab = 'skills'">
              <i class="fa fa-star" aria-hidden="true"></i> Skills
            </a>
          </li>
          <li :class="{ active: activeTab === 'users' }">
            <a href="javascript: void(0)" @click="activeTab = 'users'">
              <i class="fa fa-user" aria-hidden="true"></i> Users
            </a>
          </li>
        </ul>

        <!-- Groups Tab -->
        <div v-show="activeTab === 'groups'" class="drive-share-tab-content">
          <div class="drive-share-add-row">
            <div class="drive-share-search-wrap">
              <div class="drive-share-multi-input" @click="focusInput('groupInput')">
                <span
                  v-for="g in selectedGroups"
                  :key="g.id"
                  class="drive-share-chip"
                >
                  {{ g.name }}
                  <i class="fa fa-times" @click.stop="removeSelectedGroup(g)"></i>
                </span>
                <input
                  ref="groupInput"
                  type="text"
                  class="drive-share-inline-input"
                  placeholder="Search groups..."
                  v-model="groupSearch"
                  @input="filterGroups"
                  @focus="filterGroups"
                  @keyup.enter="submitGroups"
                />
              </div>
              <div v-if="filteredGroups.length > 0" class="drive-share-dropdown">
                <div
                  v-for="g in filteredGroups"
                  :key="g.id"
                  class="drive-share-dropdown-item"
                  @click="toggleGroup(g)"
                >
                  {{ g.name }}
                  <span class="text-muted">({{ g.members_count || 0 }} members)</span>
                </div>
              </div>
            </div>
            <label v-if="file.isFolder" class="drive-share-inherit-label">
              <input type="checkbox" v-model="newGroupInherit" /> Inherit
            </label>
            <button
              class="btn btn-success btn-sm"
              :disabled="selectedGroups.length === 0"
              @click="submitGroups"
            >
              <i class="fa fa-plus" aria-hidden="true"></i> Add
            </button>
          </div>
          <div class="drive-share-list">
            <div
              v-for="perm in groupPermissions"
              :key="perm.id"
              class="drive-share-list-item"
            >
              <span class="drive-share-list-label">
                <i class="fa fa-users" aria-hidden="true"></i>
                {{ getPermLabel(perm) }}
              </span>
              <label v-if="file.isFolder" class="drive-share-inherit-label">
                <input
                  type="checkbox"
                  :checked="perm.inherit"
                  @change="updatePerm(perm.id, { inherit: $event.target.checked })"
                /> Inherit
              </label>
              <a href="javascript: void(0)" class="drive-share-delete" @click="deletePerm(perm.id)">
                <i class="fa fa-trash-o" aria-hidden="true"></i>
              </a>
            </div>
            <div v-if="groupPermissions.length === 0" class="drive-share-empty">
              No group permissions set.
            </div>
          </div>
        </div>

        <!-- Status Tab -->
        <div v-show="activeTab === 'status'" class="drive-share-tab-content">
          <div class="drive-share-add-row">
            <div class="drive-share-search-wrap">
              <div class="drive-share-multi-input" @click="focusInput('statusInput')">
                <span
                  v-for="s in selectedStatuses"
                  :key="s.id"
                  class="drive-share-chip"
                >
                  {{ s.name }}
                  <i class="fa fa-times" @click.stop="removeSelectedStatus(s)"></i>
                </span>
                <input
                  ref="statusInput"
                  type="text"
                  class="drive-share-inline-input"
                  placeholder="Search statuses..."
                  v-model="statusSearch"
                  @input="filterStatuses"
                  @focus="filterStatuses"
                  @keyup.enter="submitStatuses"
                />
              </div>
              <div v-if="filteredStatuses.length > 0" class="drive-share-dropdown">
                <div
                  v-for="s in filteredStatuses"
                  :key="s.id"
                  class="drive-share-dropdown-item"
                  @click="toggleStatus(s)"
                >
                  {{ s.name }}
                </div>
              </div>
            </div>
            <label v-if="file.isFolder" class="drive-share-inherit-label">
              <input type="checkbox" v-model="newStatusInherit" /> Inherit
            </label>
            <button
              class="btn btn-success btn-sm"
              :disabled="selectedStatuses.length === 0"
              @click="submitStatuses"
            >
              <i class="fa fa-plus" aria-hidden="true"></i> Add
            </button>
          </div>
          <div class="drive-share-list">
            <div
              v-for="perm in statusPermissions"
              :key="perm.id"
              class="drive-share-list-item"
            >
              <span class="drive-share-list-label">
                <i class="fa fa-tag" aria-hidden="true"></i>
                {{ getPermLabel(perm) }}
              </span>
              <label v-if="file.isFolder" class="drive-share-inherit-label">
                <input
                  type="checkbox"
                  :checked="perm.inherit"
                  @change="updatePerm(perm.id, { inherit: $event.target.checked })"
                /> Inherit
              </label>
              <a href="javascript: void(0)" class="drive-share-delete" @click="deletePerm(perm.id)">
                <i class="fa fa-trash-o" aria-hidden="true"></i>
              </a>
            </div>
            <div v-if="statusPermissions.length === 0" class="drive-share-empty">
              No status-based permissions set.
            </div>
          </div>
        </div>

        <!-- Skills Tab -->
        <div v-show="activeTab === 'skills'" class="drive-share-tab-content">
          <div class="drive-share-add-row">
            <div class="drive-share-search-wrap">
              <div class="drive-share-multi-input" @click="focusInput('skillInput')">
                <span
                  v-for="sk in selectedSkills"
                  :key="sk.id"
                  class="drive-share-chip"
                >
                  {{ sk.name }}
                  <i class="fa fa-times" @click.stop="removeSelectedSkill(sk)"></i>
                </span>
                <input
                  ref="skillInput"
                  type="text"
                  class="drive-share-inline-input"
                  placeholder="Search skills..."
                  v-model="skillSearch"
                  @input="filterSkills"
                  @focus="filterSkills"
                  @keyup.enter="submitSkills"
                />
              </div>
              <div v-if="filteredSkills.length > 0" class="drive-share-dropdown">
                <div
                  v-for="sk in filteredSkills"
                  :key="sk.id"
                  class="drive-share-dropdown-item"
                  @click="toggleSkill(sk)"
                >
                  {{ sk.name }}
                </div>
              </div>
            </div>
            <label v-if="file.isFolder" class="drive-share-inherit-label">
              <input type="checkbox" v-model="newSkillInherit" /> Inherit
            </label>
            <button
              class="btn btn-success btn-sm"
              :disabled="selectedSkills.length === 0"
              @click="submitSkills"
            >
              <i class="fa fa-plus" aria-hidden="true"></i> Add
            </button>
          </div>
          <div class="drive-share-list">
            <div
              v-for="perm in skillPermissions"
              :key="perm.id"
              class="drive-share-list-item"
            >
              <span class="drive-share-list-label">
                <i class="fa fa-star" aria-hidden="true"></i>
                {{ getPermLabel(perm) }}
              </span>
              <label v-if="file.isFolder" class="drive-share-inherit-label">
                <input
                  type="checkbox"
                  :checked="perm.inherit"
                  @change="updatePerm(perm.id, { inherit: $event.target.checked })"
                /> Inherit
              </label>
              <a href="javascript: void(0)" class="drive-share-delete" @click="deletePerm(perm.id)">
                <i class="fa fa-trash-o" aria-hidden="true"></i>
              </a>
            </div>
            <div v-if="skillPermissions.length === 0" class="drive-share-empty">
              No skill-based permissions set.
            </div>
          </div>
        </div>

        <!-- Users Tab -->
        <div v-show="activeTab === 'users'" class="drive-share-tab-content">
          <div class="drive-share-add-row">
            <div class="drive-share-search-wrap">
              <div class="drive-share-multi-input" @click="focusInput('userInput')">
                <span
                  v-for="emp in selectedEmployees"
                  :key="emp.id"
                  class="drive-share-chip"
                >
                  {{ emp.first_name }} {{ emp.last_name }}
                  <i class="fa fa-times" @click.stop="removeSelectedEmployee(emp)"></i>
                </span>
                <input
                  ref="userInput"
                  type="text"
                  class="drive-share-inline-input"
                  placeholder="Search by name or email..."
                  v-model="employeeSearch"
                  @input="searchEmployees"
                  @keyup.enter="submitEmployees"
                />
              </div>
              <div v-if="employeeResults.length > 0" class="drive-share-dropdown">
                <div
                  v-for="emp in employeeResults"
                  :key="emp.id"
                  class="drive-share-dropdown-item"
                  @click="toggleEmployee(emp)"
                >
                  {{ emp.first_name }} {{ emp.last_name }}
                  <span v-if="emp.email" class="text-muted">&lt;{{ emp.email }}&gt;</span>
                </div>
              </div>
            </div>
            <label v-if="file.isFolder" class="drive-share-inherit-label">
              <input type="checkbox" v-model="newUserInherit" /> Inherit
            </label>
            <button
              class="btn btn-success btn-sm"
              :disabled="selectedEmployees.length === 0"
              @click="submitEmployees"
            >
              <i class="fa fa-plus" aria-hidden="true"></i> Add
            </button>
          </div>
          <!-- Existing user permissions -->
          <div class="drive-share-list">
            <div
              v-for="perm in userPermissions"
              :key="perm.id"
              class="drive-share-list-item"
            >
              <span class="drive-share-list-label">
                <i class="fa fa-user" aria-hidden="true"></i>
                {{ getPermLabel(perm) }}
              </span>
              <label v-if="file.isFolder" class="drive-share-inherit-label">
                <input
                  type="checkbox"
                  :checked="perm.inherit"
                  @change="updatePerm(perm.id, { inherit: $event.target.checked })"
                /> Inherit
              </label>
              <a href="javascript: void(0)" class="drive-share-delete" @click="deletePerm(perm.id)">
                <i class="fa fa-trash-o" aria-hidden="true"></i>
              </a>
            </div>
            <div v-if="userPermissions.length === 0" class="drive-share-empty">
              No user permissions set.
            </div>
          </div>
        </div>
      </div>
      <div class="modal-footer text-center">
        <a
          href="javascript: void(0)"
          class="c-solid-btn"
          @click="closeModal"
        >
          Close
        </a>
      </div>
    </div>
  </modal>
</template>

<script>
import service from "../../services.js";

export default {
  name: "DriveShareDialog",
  props: {
    file: { type: Object, required: true },
    apiHeaders: { type: Object, required: true },
  },
  data: function () {
    return {
      activeTab: "groups",
      loadingPerms: false,
      permissions: [],

      // Users tab
      employeeSearch: "",
      employeeResults: [],
      selectedEmployees: [],
      newUserRole: "reader",
      newUserInherit: false,
      searchTimer: null,

      // Groups tab
      groups: [],
      groupSearch: "",
      filteredGroups: [],
      selectedGroups: [],
      newGroupRole: "reader",
      newGroupInherit: false,

      // Status tab
      statuses: [],
      statusSearch: "",
      filteredStatuses: [],
      selectedStatuses: [],
      newStatusRole: "reader",
      newStatusInherit: false,

      // Skills tab
      skills: [],
      skillSearch: "",
      filteredSkills: [],
      selectedSkills: [],
      newSkillRole: "reader",
      newSkillInherit: false,

      // File restrictions
      downloadRestricted: false,
      visibilityRestricted: false,
      restrictionInherit: false,
      restrictionLoading: false,
    };
  },
  computed: {
    userPermissions: function () {
      return this.permissions.filter(function (p) {
        return p.permissible_type === "App\\Employee";
      });
    },
    groupPermissions: function () {
      return this.permissions.filter(function (p) {
        return p.permissible_type === "App\\DriveGroup";
      });
    },
    statusPermissions: function () {
      return this.permissions.filter(function (p) {
        return p.permissible_type === "status";
      });
    },
    skillPermissions: function () {
      return this.permissions.filter(function (p) {
        return p.permissible_type === "skill";
      });
    },
  },
  mounted: function () {
    this.$modal.show("drive-share");
    this.loadPermissions();
    this.loadRestriction();
    this.loadGroups();
    this.loadStatuses();
    this.loadSkills();
  },
  methods: {
    focusInput: function (ref) {
      if (this.$refs[ref]) {
        this.$refs[ref].focus();
      }
    },

    isSelected: function (arr, item) {
      return arr.some(function (x) { return x.id === item.id; });
    },

    loadPermissions: function () {
      var self = this;
      this.loadingPerms = true;
      service
        .get("drive/permissions/" + encodeURIComponent(this.file.id), this.apiHeaders)
        .then(function (res) {
          self.loadingPerms = false;
          if (res.data.status_code === "1") {
            self.permissions = res.data.result || [];
          }
        })
        .catch(function () {
          self.loadingPerms = false;
        });
    },

    // ---- Download Restriction ----
    loadRestriction: function () {
      var self = this;
      this.restrictionLoading = true;
      service
        .get("drive/restrictions/" + encodeURIComponent(this.file.id), this.apiHeaders)
        .then(function (res) {
          self.restrictionLoading = false;
          if (res.data.status_code === "1") {
            self.downloadRestricted = res.data.result.restricted || false;
            self.visibilityRestricted = res.data.result.restrict_visibility || false;
            self.restrictionInherit = res.data.result.inherit || false;
          }
        })
        .catch(function () {
          self.restrictionLoading = false;
        });
    },

    onRestrictionToggle: function () {
      var self = this;
      this.restrictionLoading = true;
      var hasAny = this.downloadRestricted || this.visibilityRestricted;
      var inherit = hasAny ? this.restrictionInherit : false;

      service
        .put(
          "drive/restrictions/" + encodeURIComponent(this.file.id),
          {
            restricted: this.downloadRestricted,
            restrict_visibility: this.visibilityRestricted,
            inherit: inherit,
          },
          this.apiHeaders
        )
        .then(function (res) {
          self.restrictionLoading = false;
          if (res.data.status_code === "1") {
            self.downloadRestricted = res.data.result.restricted;
            self.visibilityRestricted = res.data.result.restrict_visibility;
            self.restrictionInherit = res.data.result.inherit;
            self.$message({ type: "success", message: "Restrictions updated" });
            self.$emit("permissions-changed");
          } else {
            self.$message({ type: "error", message: res.data.status_message || "Failed" });
            self.loadRestriction();
          }
        })
        .catch(function () {
          self.restrictionLoading = false;
          self.$message({ type: "error", message: "Failed to update restrictions" });
          self.loadRestriction();
        });
    },

    onRestrictionInheritChange: function (checked) {
      var self = this;
      this.restrictionLoading = true;

      service
        .put(
          "drive/restrictions/" + encodeURIComponent(this.file.id),
          {
            restricted: this.downloadRestricted,
            restrict_visibility: this.visibilityRestricted,
            inherit: checked,
          },
          this.apiHeaders
        )
        .then(function (res) {
          self.restrictionLoading = false;
          if (res.data.status_code === "1") {
            self.restrictionInherit = res.data.result.inherit;
            self.$message({ type: "success", message: "Inheritance updated" });
            self.$emit("permissions-changed");
          } else {
            self.$message({ type: "error", message: res.data.status_message || "Failed" });
          }
        })
        .catch(function () {
          self.restrictionLoading = false;
          self.$message({ type: "error", message: "Failed to update inheritance" });
        });
    },

    // ---- Groups ----
    loadGroups: function () {
      var self = this;
      service
        .get("drive/groups", this.apiHeaders)
        .then(function (res) {
          if (res.data.status_code === "1") {
            self.groups = res.data.result || [];
          }
        })
        .catch(function () {});
    },

    filterGroups: function () {
      var self = this;
      var search = (this.groupSearch || "").toLowerCase();
      var available = this.groups.filter(function (g) {
        return !self.isSelected(self.selectedGroups, g);
      });
      if (!search) {
        this.filteredGroups = available;
        return;
      }
      this.filteredGroups = available.filter(function (g) {
        return (g.name || "").toLowerCase().indexOf(search) !== -1;
      });
    },

    toggleGroup: function (g) {
      if (this.isSelected(this.selectedGroups, g)) {
        this.selectedGroups = this.selectedGroups.filter(function (x) { return x.id !== g.id; });
      } else {
        this.selectedGroups.push(g);
      }
      this.groupSearch = "";
      this.filteredGroups = [];
      this.focusInput("groupInput");
    },

    removeSelectedGroup: function (g) {
      this.selectedGroups = this.selectedGroups.filter(function (x) { return x.id !== g.id; });
    },

    submitGroups: function () {
      if (this.selectedGroups.length === 0) return;
      var self = this;
      var items = this.selectedGroups.slice();
      this.addPermissionsBatch("App\\DriveGroup", items, this.newGroupRole, this.newGroupInherit, function () {
        self.selectedGroups = [];
        self.groupSearch = "";
        self.filteredGroups = [];
        self.newGroupRole = "reader";
        self.newGroupInherit = false;
      });
    },

    // ---- Statuses ----
    loadStatuses: function () {
      var self = this;
      service
        .get("employee/employee_status", this.apiHeaders)
        .then(function (res) {
          self.statuses = (res.data && res.data.result) || [];
        })
        .catch(function () {});
    },

    filterStatuses: function () {
      var self = this;
      var search = (this.statusSearch || "").toLowerCase();
      var available = this.statuses.filter(function (s) {
        return !self.isSelected(self.selectedStatuses, s);
      });
      if (!search) {
        this.filteredStatuses = available;
        return;
      }
      this.filteredStatuses = available.filter(function (s) {
        return (s.name || "").toLowerCase().indexOf(search) !== -1;
      });
    },

    toggleStatus: function (s) {
      if (this.isSelected(this.selectedStatuses, s)) {
        this.selectedStatuses = this.selectedStatuses.filter(function (x) { return x.id !== s.id; });
      } else {
        this.selectedStatuses.push(s);
      }
      this.statusSearch = "";
      this.filteredStatuses = [];
      this.focusInput("statusInput");
    },

    removeSelectedStatus: function (s) {
      this.selectedStatuses = this.selectedStatuses.filter(function (x) { return x.id !== s.id; });
    },

    submitStatuses: function () {
      if (this.selectedStatuses.length === 0) return;
      var self = this;
      var items = this.selectedStatuses.slice();
      this.addPermissionsBatch("status", items, this.newStatusRole, this.newStatusInherit, function () {
        self.selectedStatuses = [];
        self.statusSearch = "";
        self.filteredStatuses = [];
        self.newStatusRole = "reader";
        self.newStatusInherit = false;
      });
    },

    // ---- Skills ----
    loadSkills: function () {
      var self = this;
      service
        .get("skill/getAllSkills", this.apiHeaders)
        .then(function (res) {
          self.skills = (res.data && res.data.result) || [];
        })
        .catch(function () {});
    },

    filterSkills: function () {
      var self = this;
      var search = (this.skillSearch || "").toLowerCase();
      var available = this.skills.filter(function (sk) {
        return !self.isSelected(self.selectedSkills, sk);
      });
      if (!search) {
        this.filteredSkills = available;
        return;
      }
      this.filteredSkills = available.filter(function (sk) {
        return (sk.name || "").toLowerCase().indexOf(search) !== -1;
      });
    },

    toggleSkill: function (sk) {
      if (this.isSelected(this.selectedSkills, sk)) {
        this.selectedSkills = this.selectedSkills.filter(function (x) { return x.id !== sk.id; });
      } else {
        this.selectedSkills.push(sk);
      }
      this.skillSearch = "";
      this.filteredSkills = [];
      this.focusInput("skillInput");
    },

    removeSelectedSkill: function (sk) {
      this.selectedSkills = this.selectedSkills.filter(function (x) { return x.id !== sk.id; });
    },

    submitSkills: function () {
      if (this.selectedSkills.length === 0) return;
      var self = this;
      var items = this.selectedSkills.slice();
      this.addPermissionsBatch("skill", items, this.newSkillRole, this.newSkillInherit, function () {
        self.selectedSkills = [];
        self.skillSearch = "";
        self.filteredSkills = [];
        self.newSkillRole = "reader";
        self.newSkillInherit = false;
      });
    },

    // ---- Users (employees) ----
    searchEmployees: function () {
      var self = this;
      if (this.searchTimer) {
        clearTimeout(this.searchTimer);
      }
      if (!this.employeeSearch || this.employeeSearch.length < 2) {
        this.employeeResults = [];
        return;
      }
      this.searchTimer = setTimeout(function () {
        var headers = Object.assign({}, self.apiHeaders, {
          searchEmployee: self.employeeSearch,
        });
        service
          .get("employee/searchEmployee", headers)
          .then(function (res) {
            var results = (res.data && res.data.result) || [];
            self.employeeResults = results.filter(function (emp) {
              return !self.isSelected(self.selectedEmployees, emp);
            });
          })
          .catch(function () {
            self.employeeResults = [];
          });
      }, 300);
    },

    toggleEmployee: function (emp) {
      if (this.isSelected(this.selectedEmployees, emp)) {
        this.selectedEmployees = this.selectedEmployees.filter(function (x) { return x.id !== emp.id; });
      } else {
        this.selectedEmployees.push(emp);
      }
      this.employeeSearch = "";
      this.employeeResults = [];
      this.focusInput("userInput");
    },

    removeSelectedEmployee: function (emp) {
      this.selectedEmployees = this.selectedEmployees.filter(function (x) { return x.id !== emp.id; });
    },

    submitEmployees: function () {
      if (this.selectedEmployees.length === 0) return;
      var self = this;
      var items = this.selectedEmployees.slice();
      this.addPermissionsBatch("App\\Employee", items, this.newUserRole, this.newUserInherit, function () {
        self.selectedEmployees = [];
        self.employeeSearch = "";
        self.employeeResults = [];
        self.newUserRole = "reader";
        self.newUserInherit = false;
      });
    },

    // ---- Permissions CRUD ----
    addPermissionsBatch: function (permissibleType, items, role, inherit, onDone) {
      var self = this;
      this.loadingPerms = true;
      var pending = items.length;
      var failed = 0;

      items.forEach(function (item) {
        service
          .post(
            "drive/permissions/" + encodeURIComponent(self.file.id),
            {
              permissible_type: permissibleType,
              permissible_id: item.id,
              role: role,
              inherit: inherit,
            },
            self.apiHeaders
          )
          .then(function (res) {
            if (res.data.status_code !== "1") {
              failed++;
            }
            pending--;
            if (pending === 0) {
              self.loadingPerms = false;
              if (failed === 0) {
                self.$message({ type: "success", message: items.length + " permission(s) added" });
              } else {
                self.$message({ type: "warning", message: (items.length - failed) + " added, " + failed + " failed" });
              }
              self.loadPermissions();
              self.$emit("permissions-changed");
              if (onDone) onDone();
            }
          })
          .catch(function () {
            failed++;
            pending--;
            if (pending === 0) {
              self.loadingPerms = false;
              self.$message({ type: "error", message: failed + " permission(s) failed to add" });
              self.loadPermissions();
              if (onDone) onDone();
            }
          });
      });
    },

    updatePerm: function (permId, data) {
      var self = this;
      service
        .put("drive/permissions/" + encodeURIComponent(permId), data, this.apiHeaders)
        .then(function (res) {
          if (res.data.status_code === "1") {
            self.$message({ type: "success", message: "Permission updated" });
            self.loadPermissions();
            self.$emit("permissions-changed");
          } else {
            self.$message({ type: "error", message: res.data.status_message || "Update failed" });
          }
        })
        .catch(function () {
          self.$message({ type: "error", message: "Update failed" });
        });
    },

    deletePerm: function (permId) {
      var self = this;
      this.$confirm("Remove this permission?", "Confirm", {
        confirmButtonText: "Remove",
        cancelButtonText: "Cancel",
        type: "warning",
      })
        .then(function () {
          service
            .del("drive/permissions/" + encodeURIComponent(permId), self.apiHeaders)
            .then(function (res) {
              if (res.data.status_code === "1") {
                self.$message({ type: "success", message: "Permission removed" });
                self.loadPermissions();
                self.$emit("permissions-changed");
              } else {
                self.$message({ type: "error", message: res.data.status_message || "Delete failed" });
              }
            })
            .catch(function () {
              self.$message({ type: "error", message: "Delete failed" });
            });
        })
        .catch(function () {});
    },

    getPermLabel: function (perm) {
      if (perm.permissible_type === "App\\Employee") {
        if (perm.permissible) {
          var user = perm.permissible.user || perm.permissible;
          var name = (user.first_name || "") + " " + (user.last_name || "");
          return name.trim() || ("Employee #" + perm.permissible_id);
        }
        return "Employee #" + (perm.permissible_id || "").toString().substring(0, 8);
      }
      if (perm.permissible_type === "App\\DriveGroup") {
        var group = this.groups.find(function (g) { return g.id === perm.permissible_id; });
        return "Group: " + (group ? group.name : "#" + perm.permissible_id);
      }
      if (perm.permissible_type === "status") {
        var status = this.statuses.find(function (s) { return s.id === perm.permissible_id; });
        return "Status: " + (status ? status.name : "#" + perm.permissible_id);
      }
      if (perm.permissible_type === "skill") {
        var skill = this.skills.find(function (s) { return s.id === perm.permissible_id; });
        return "Skill: " + (skill ? skill.name : "#" + perm.permissible_id);
      }
      return perm.permissible_type + ": " + perm.permissible_id;
    },

    closeModal: function () {
      this.$modal.hide("drive-share");
      this.$emit("close");
    },
  },
};
</script>

<style scoped>
.drive-share-tabs {
  margin-bottom: 15px;
}
.drive-share-tab-content {
  min-height: 200px;
}
.drive-share-add-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 12px;
  flex-wrap: wrap;
}
.drive-share-add-row .form-control {
  flex: 1;
  min-width: 120px;
}
.drive-share-search-wrap {
  flex: 2;
  position: relative;
  min-width: 180px;
}
.drive-share-multi-input {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
  min-height: 34px;
  padding: 4px 8px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  background: #fff;
  cursor: text;
}
.drive-share-multi-input:focus-within {
  border-color: #409eff;
}
.drive-share-inline-input {
  border: none;
  outline: none;
  flex: 1;
  min-width: 80px;
  font-size: 13px;
  padding: 2px 0;
  background: transparent;
}
.drive-share-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  background: #e8f0fe;
  border-radius: 12px;
  font-size: 12px;
  white-space: nowrap;
  color: #1a73e8;
}
.drive-share-chip i {
  cursor: pointer;
  font-size: 10px;
  color: #999;
}
.drive-share-chip i:hover {
  color: #c0392b;
}
.drive-share-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: #fff;
  border: 1px solid #ddd;
  border-top: none;
  border-radius: 0 0 4px 4px;
  max-height: 200px;
  overflow-y: auto;
  z-index: 1000;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}
.drive-share-dropdown-item {
  padding: 8px 12px;
  cursor: pointer;
  font-size: 13px;
}
.drive-share-dropdown-item:hover {
  background: #f5f7fa;
}
.drive-share-role-select {
  max-width: 130px;
  flex: 0 0 130px !important;
  height: 34px;
}
.drive-share-inherit-label {
  white-space: nowrap;
  font-weight: normal;
  margin: 0;
  font-size: 13px;
  padding-top: 7px;
}
.drive-share-list {
  border: 1px solid #eee;
  border-radius: 4px;
  max-height: 250px;
  overflow-y: auto;
}
.drive-share-list-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-bottom: 1px solid #f5f5f5;
  font-size: 13px;
}
.drive-share-list-item:last-child {
  border-bottom: none;
}
.drive-share-list-label {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.drive-share-role-inline {
  width: 120px !important;
  flex: 0 0 120px !important;
}
.drive-share-delete {
  color: #c0392b;
  font-size: 14px;
}
.drive-share-delete:hover {
  color: #e74c3c;
}
.drive-share-empty {
  padding: 20px;
  text-align: center;
  color: #999;
  font-size: 13px;
}
.drive-share-restriction-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 0;
}
.drive-share-restriction-label {
  font-weight: 500;
  font-size: 14px;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 6px;
}
</style>
